import axios from "axios";
import { specificErrorCodes, TransactionFailedErrorType } from "./constants";
import i18n from "i18next";

const BASE_URL = process.env.REACT_APP_API_URL;

const { t } = i18n;

// `accessToken` (when provided) is sent both as `Authorization: Bearer <token>`
// (the new, preferred path) AND as `params.access_token` (the legacy path).
// The BE accepts both during the LT-450705 rollout; the legacy query param is
// kept as a silent fallback so a hypothetical bug in the header path can't
// 401 every widget request. A follow-up PR will drop the legacy param once
// header-path traffic has been verified in production.
const normalizeConfig = ({ accessToken, params, headers } = {}) => {
  return {
    params: {
      ...(params || {}),
      ...(accessToken ? { access_token: accessToken } : {}),
      locale: localStorage.getItem("i18nextLng") || "en",
    },
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(headers || {}),
    },
  };
};

const get = async (path, config = {}) => {
  try {
    const response = await axios.get(
      `${BASE_URL}${path}`,
      normalizeConfig(config)
    );
    return { data: response.data, error: null };
  } catch (err) {
    return handleError(err);
  }
};

const post = async (path, data, config = {}) => {
  try {
    const response = await axios.post(
      `${BASE_URL}${path}`,
      data,
      normalizeConfig(config)
    );
    return { data: response.data, error: null };
  } catch (err) {
    return handleError(err);
  }
};

const put = async (path, data, config = {}) => {
  try {
    const response = await axios.put(
      `${BASE_URL}${path}`,
      data,
      normalizeConfig(config)
    );
    return { data: response.data, error: null };
  } catch (err) {
    return handleError(err);
  }
};

const del = async (path, config = {}) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}${path}`,
      normalizeConfig(config)
    );
    return { data: response.data, error: null };
  } catch (err) {
    return handleError(err);
  }
};

const getErrorMessage = (err) => {
  // handling payment declined errors
  const specificErrorMessage =
    err.response?.data?.errors?.general?.[0]?.general?.[0];
  if (
    err.response?.data?.errors?.general?.[0]?.type ===
      TransactionFailedErrorType &&
    specificErrorMessage
  ) {
    return specificErrorMessage;
  }

  const message =
    err.response?.data?.errors?.general?.[0]?.type ||
    err.response?.data?.error ||
    err.message ||
    null;

  return specificErrorCodes.includes(message)
    ? t(`errors.${message}`)
    : message;
};

const getErrorType = (err) => {
  return err.response?.data?.errors?.general?.[0]?.type;
};

const handleError = (err) => {
  return {
    data: null,
    error: {
      status: err.response?.status,
      message: getErrorMessage(err),
      type: getErrorType(err),
      raw: err,
    },
  };
};

const ApiWrapper = {
  get,
  post,
  put,
  delete: del,
};

export default ApiWrapper;
